/**
 * DEV-2521: request-body construction for `POST /api/owner/reservation/booking`.
 *
 * `buildStayBookingBody` reproduces the legacy Owner / Guest of Owner payload exactly.
 * `buildMaintenanceBookingBody` produces the new maintenance payload, which deliberately
 * excludes guest, cleaning, stage and identity fields.
 */
import { BookingKind, MaintenanceType, bookingKindToApiType } from './bookingKinds';

/** Maximum notes length accepted by the existing Salesforce long-text field. */
export const SPECIAL_REQUESTS_MAX_LENGTH = 32768;

/**
 * Fold the portal-only vendor/company name into notes for the maintenance create payload.
 * Format: `Vendor: [name]` then a single line break, then any additional notes.
 * Blank vendor names leave notes unchanged.
 */
export function mergeVendorNameIntoNotes(
  notes: string | null | undefined,
  vendorName: string | null | undefined
): string {
  const detail = typeof notes === 'string' ? notes : '';
  const vendor = typeof vendorName === 'string' ? vendorName.trim() : '';
  if (!vendor) {
    return detail;
  }
  const prefix = `Vendor: ${vendor}`;
  const trimmedDetail = detail.trim();
  return trimmedDetail.length > 0 ? `${prefix}\n${detail}` : prefix;
}

export interface StayBookingArgs {
  check_in: Date | string;
  check_out: Date | string;
  listing: { id: string };
  guest_is_owner: string | boolean;
  guest_pay_final_cleaning: string | boolean;
  no_departure_clean: string | boolean;
  special_requests_detail: string;
  externalAccountID?: string;
  guest_email?: string;
  guest_first_name?: string;
  guest_last_name?: string;
  guest_phone?: string;
}

/**
 * Legacy stay payload. Field order, coercions and conditionals are unchanged from the
 * original inline implementation in the RTK Query mutation (R1 compatibility).
 */
export function buildStayBookingBody(args: StayBookingArgs): Record<string, unknown> {
  //NOTE: We have to carefully check our types here! It's not bool, due to the RHF 'checkboxes' (radios).
  //		They give us string "true"/"false" (and it's not trivial to re-cast that on the way in).
  const boolGuestIsOwnerArg = args.guest_is_owner === 'true' || args.guest_is_owner === true;
  const boolOwnerPayFinalClean =
    args.guest_pay_final_cleaning === 'false' || args.guest_pay_final_cleaning === false;
  const boolNoDepartureClean =
    args.no_departure_clean === 'true' || args.no_departure_clean === true;

  const body: any = {
    check_in: args.check_in,
    check_out: args.check_out,
    listing_id: args.listing.id,
    no_departure_clean: boolNoDepartureClean,
    type: boolGuestIsOwnerArg ? 'Owner' : 'Guest of Owner',
    special_requests_detail: args.special_requests_detail
  };

  if (boolGuestIsOwnerArg) {
    body.guest_id = args.externalAccountID;
    body.owner_pays_guest_clean = true;
  } else {
    if (!args.guest_email || !args.guest_first_name || !args.guest_last_name) {
      throw new Error('New booking guest info incomplete.');
    }

    body.guest_email = args.guest_email;
    body.guest_first_name = args.guest_first_name;
    body.guest_last_name = args.guest_last_name;
    if (args.guest_phone) {
      body.guest_phone = args.guest_phone;
    }
    body.owner_pays_guest_clean = boolOwnerPayFinalClean;
  }

  body.stage = 'Confirmed';

  return body;
}

export interface MaintenanceBookingArgs {
  listing: { id: string };
  maintenance_type: MaintenanceType | null | undefined;
  /** Explicit-offset ISO timestamp in the property zone. */
  check_in: string;
  /** Explicit-offset ISO timestamp in the property zone. */
  check_out: string;
  special_requests_detail?: string | null;
}

export interface MaintenanceBookingBody {
  listing_id: string;
  type: string;
  check_in: string;
  check_out: string;
  special_requests_detail?: string;
}

const OFFSET_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

/**
 * Maintenance payload: `listing_id`, `type`, `check_in`, `check_out`, optional notes. Any
 * guest, cleaning, stage or identity value present on the form is intentionally dropped.
 */
export function buildMaintenanceBookingBody(args: MaintenanceBookingArgs): MaintenanceBookingBody {
  if (!args.listing || !args.listing.id) {
    throw new Error('A maintenance block requires a property.');
  }
  const type = bookingKindToApiType('maintenance', args.maintenance_type);
  if (!OFFSET_TIMESTAMP_PATTERN.test(args.check_in) || !OFFSET_TIMESTAMP_PATTERN.test(args.check_out)) {
    throw new Error('Maintenance timestamps must include an explicit offset.');
  }
  const body: MaintenanceBookingBody = {
    listing_id: args.listing.id,
    type,
    check_in: args.check_in,
    check_out: args.check_out
  };
  const notes = typeof args.special_requests_detail === 'string' ? args.special_requests_detail : '';
  if (notes.length > SPECIAL_REQUESTS_MAX_LENGTH) {
    throw new Error('Notes are too long.');
  }
  // Blank notes are omitted rather than sent as an empty string; non-blank notes are sent
  // exactly as typed (no trimming or truncation).
  if (notes.trim().length > 0) {
    body.special_requests_detail = notes;
  }
  return body;
}

/** Discriminated argument shape passed to the RTK Query booking mutation. */
export type PostReservationArgs =
  | ({ bookingKind?: Exclude<BookingKind, 'maintenance'> } & StayBookingArgs)
  | ({ bookingKind: 'maintenance' } & MaintenanceBookingArgs);

export function isMaintenanceBookingArgs(
  args: PostReservationArgs
): args is { bookingKind: 'maintenance' } & MaintenanceBookingArgs {
  return args.bookingKind === 'maintenance';
}
